import { useMemo, useReducer, useState } from "react"

import {
  formatDeviceAlerts,
  formatPowerlineAlerts,
  formatTowerAlerts,
  getAlertAmounts,
  getFilteredSortedAlerts,
  sortAndGroupAlerts,
} from "helpers/formatters/alertSettingsFormatters"
import useAlertSettings from "helpers/hooks/useAlertSettings"
import useAlertSettingsWStates from "helpers/hooks/useAlertSettingsStates"
import useDevices from "helpers/hooks/useDevices"
import { multipleDateRangeReducer } from "helpers/reducers/dateRangeReducer"
import type { AlertsFilterOptions } from "types/alerts.types"
import AlertsContext from "widgets/alerts/AlertsContext"
import { SectionContainer } from "widgets/styled/containers"
import Spinner from "widgets/common/Spinner"
import AlertsFilter from "widgets/alerts/AlertsFilter"
import AccordionsSection from "widgets/alerts/AccordionsSection"
import {
  getPowerlineIdsFromSettings,
  getTowerIdsFromSettings,
} from "helpers/utils/alerts"
import useSelectedPowerlines from "helpers/hooks/powerlines/useSelectedPowerlines"
import useSelectedTowers from "helpers/hooks/towers/useSelectedTowers"

function AlertsContent() {
  const {
    orgDevicesAlertSettingsWStates,
    orgPowerlinesAlertSettingsWStates,
    orgTowersAlertSettingsWStates,
    isLoading: settingsStatesLoading,
  } = useAlertSettingsWStates()
  const {
    orgActiveDevicesAlertSettingsById,
    orgActivePowerlinesAlertSettings,
    orgActivePowerlinesAlertSettingsById,
    orgActiveTowersAlertSettings,
    orgActiveTowersAlertSettingsById,
    isLoading: settingsLoading,
  } = useAlertSettings()
  const { devsWRelsById, isLoading: devicesLoading } = useDevices()
  const [multipleDateRange, dispatchMultipleDateRange] = useReducer(
    multipleDateRangeReducer,
    {},
  )
  const [filters, setFilters] = useState<AlertsFilterOptions | null>(null)

  const powerlineIds = useMemo(
    () => getPowerlineIdsFromSettings(orgActivePowerlinesAlertSettings),
    [orgActivePowerlinesAlertSettings],
  )
  const { powerlinesById, powerlinesLoading } = useSelectedPowerlines({ powerlineIds })

  const towerIds = useMemo(
    () => getTowerIdsFromSettings(orgActiveTowersAlertSettings),
    [orgActiveTowersAlertSettings],
  )
  const { towersById, towersLoading } = useSelectedTowers({ towerIds })

  const formattedDeviceAlerts = useMemo(
    () =>
      formatDeviceAlerts(
        orgDevicesAlertSettingsWStates,
        orgActiveDevicesAlertSettingsById,
        devsWRelsById,
      ),
    [orgDevicesAlertSettingsWStates, orgActiveDevicesAlertSettingsById, devsWRelsById],
  )

  const formattedPowerlineAlerts = useMemo(
    () =>
      formatPowerlineAlerts(
        orgPowerlinesAlertSettingsWStates,
        orgActivePowerlinesAlertSettingsById,
        powerlinesById,
      ),
    [
      orgPowerlinesAlertSettingsWStates,
      orgActivePowerlinesAlertSettingsById,
      powerlinesById,
    ],
  )

  const formattedTowerAlerts = useMemo(
    () =>
      formatTowerAlerts(
        orgTowersAlertSettingsWStates,
        orgActiveTowersAlertSettingsById,
        towersById,
      ),
    [orgTowersAlertSettingsWStates, orgActiveTowersAlertSettingsById, towersById],
  )

  const sortedAndGroupedAlerts = useMemo(
    () =>
      sortAndGroupAlerts([
        ...formattedDeviceAlerts,
        ...formattedPowerlineAlerts,
        ...formattedTowerAlerts,
      ]),
    [formattedDeviceAlerts, formattedPowerlineAlerts, formattedTowerAlerts],
  )

  const alertsAmount = useMemo(
    () => getAlertAmounts(sortedAndGroupedAlerts),
    [sortedAndGroupedAlerts],
  )
  const alertsSortedByState = useMemo(
    () => getFilteredSortedAlerts(sortedAndGroupedAlerts, filters),
    [sortedAndGroupedAlerts, filters],
  )

  const isLoading = useMemo(
    () =>
      settingsStatesLoading ||
      settingsLoading ||
      devicesLoading ||
      powerlinesLoading ||
      towersLoading,
    [
      devicesLoading,
      powerlinesLoading,
      towersLoading,
      settingsLoading,
      settingsStatesLoading,
    ],
  )

  return (
    <AlertsContext.Provider
      value={{
        multipleDateRange,
        dispatchMultipleDateRange,
      }}
    >
      <SectionContainer disableGutters maxWidth="xl">
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <AlertsFilter
              alertsAmount={alertsAmount}
              filters={filters}
              setFilters={setFilters}
              defaultValues={["open", "silenced"]}
            />
            <AccordionsSection alerts={alertsSortedByState} filters={filters} />
          </>
        )}
      </SectionContainer>
    </AlertsContext.Provider>
  )
}

export default AlertsContent
